/**
 * Brand Bible page layout primitives: the completeness ring and the section-meta-aware
 * header. Moved out of BrandBiblePage.tsx (3,000-line rule) with no behaviour change; the
 * page re-exports SectionHeader because the section components still import it from there.
 */
import { BRAND_BIBLE_SECTION_BY_ID } from '@/components/brand-bible/BrandBibleSectionNavigation';
import { SectionHeader as BbSectionHeader } from '@/components/brand-bible/formPrimitives';
import type { SectionId } from '@/lib/bbSectionMap';

export function ProgressRing({ pct }: { pct: number }) {
  const r = 14;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  const stroke = pct === 100 ? 'hsl(var(--status-success))' : 'var(--color-coral)';
  return (
    <div className="relative w-9 h-9">
      <svg width="36" height="36" viewBox="0 0 36 36">
        <circle cx="18" cy="18" r={r} fill="none" stroke="hsl(var(--border))" strokeWidth="3" />
        <circle
          cx="18"
          cy="18"
          r={r}
          fill="none"
          stroke={stroke}
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          transform="rotate(-90 18 18)"
          className="transition-[stroke-dasharray] duration-500"
        />
      </svg>
      <div
        className="absolute inset-0 grid place-items-center text-[10px] font-bold font-mono"
        style={{ color: stroke }}
      >
        {pct}
      </div>
    </div>
  );
}

interface SectionHeaderProps {
  /** The section this header belongs to; letter/icon/hint/title come from SECTIONS. */
  id: SectionId;
  right?: React.ReactNode;
}

/**
 * Section-meta-aware wrapper over the shared generic header
 * (components/brand-bible/formPrimitives): looks up letter/icon/hint/title
 * from SECTIONS so page call sites keep passing just the section id.
 */
export function SectionHeader({ id, right }: SectionHeaderProps) {
  const { letter, icon, hint, title } = BRAND_BIBLE_SECTION_BY_ID[id];
  return <BbSectionHeader letter={letter} icon={icon} hint={hint} title={title} right={right} />;
}
